import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  LayoutDashboard,
  FolderTree,
  Layers,
  FileText,
  BookOpen,
  Users,
  LogOut,
  Menu,
  X,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/presentation/hook/useAuth';
import { Btn } from '@/presentation/component/ui';

const SIDEBAR_LINKS = [
  { to: '/admin', icon: LayoutDashboard, key: 'admin.dashboard' },
  { to: '/admin/categories', icon: FolderTree, key: 'admin.categories' },
  { to: '/admin/subcategories', icon: Layers, key: 'admin.subcategoriesNav' },
  { to: '/admin/chapters', icon: FileText, key: 'admin.chaptersNav' },
  { to: '/admin/courses', icon: BookOpen, key: 'admin.courses' },
  { to: '/admin/users', icon: Users, key: 'admin.users' },
] as const;

export const AdminLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navLinks = SIDEBAR_LINKS.map(({ to, icon: Icon, key }) => (
    <Link
      key={to}
      to={to}
      onClick={() => setIsMobileMenuOpen(false)}
      className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
        isActive(to)
          ? 'text-[var(--color-accent)] bg-[var(--color-accent-soft)]'
          : 'text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-2)]'
      }`}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {t(key, { defaultValue: key.split('.')[1] })}
    </Link>
  ));

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <header className="bg-[var(--color-surface)] shadow-sm border-b border-[var(--color-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                className="md:hidden p-2 rounded-md text-[var(--color-ink-soft)] hover:text-[var(--color-accent)]"
                aria-label={t('nav.menu', { defaultValue: 'Menu' })}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <Link to="/admin" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--color-accent)]">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold text-[var(--color-ink)]">
                  DaaraJi {t('admin.title', { defaultValue: 'Admin' })}
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--color-ink-soft)] hidden sm:inline">
                {t('nav.hello')}, {user?.name}
              </span>
              <Btn size="sm" onClick={handleLogout} icon={<LogOut className="h-4 w-4" />}>
                {t('nav.logout')}
              </Btn>
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-[var(--color-surface)] border-b border-[var(--color-line)] px-4 py-3">
          <nav className="space-y-1">{navLinks}</nav>
        </div>
      )}

      <div className="flex">
        <aside className="w-64 bg-[var(--color-surface)] shadow-sm min-h-[calc(100vh-4rem)] border-r border-[var(--color-line)] hidden md:block">
          <nav className="mt-4 px-3 space-y-1">{navLinks}</nav>
        </aside>

        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};
